import { InspectionScopeItemGridRowType, InspectionScopeItemType, ReportType } from '@inspectify/core';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { MutateInspectionScopeItemMutation } from "../../../gql/generated/queries";

export interface DrawBudgetState {
  rows: InspectionScopeItemGridRowType[];
}

const initialState: DrawBudgetState = {
  rows: [],
};

export const drawBudgetSlice = createSlice({
  name: 'drawBudget',
  initialState,
  reducers: {
    setDrawBudget: (state, action) => {
      if (!action.payload) return state;
      const { inspectionReports, reportSystems } = action.payload as ReportType;
      const scopeItems = [...(inspectionReports[0].inspection?.inspectionScopeItems || [])]
        .sort((a: InspectionScopeItemType, b: InspectionScopeItemType) => {
          const aIndex = a.displayIndex ?? -1;
          const bIndex = b.displayIndex ?? -1;

          return aIndex - bIndex || a.orderUnit?.name?.localeCompare(b?.orderUnit?.name || '') || 0;
        });

      const informations = reportSystems.flatMap((system) => system.reportSubsystems?.flatMap((subsystem) => subsystem.reportInformations));

      const rows = scopeItems.map((scope) => {
        const { budget, clientDisbursement } = scope;
        const information = informations.find((info) => info.inspectionScopeItemId === scope.id);
        const informationProgressValue = parseInt(information?.value || '0', 10) || 0;
        return ({
          ...scope,
          reportSubsystemId: information?.reportSubsystemId,
          budget: scope.budget,
          disbursement: scope.disbursement,
          previousDisbursement: scope.previousDisbursement,
          clientDisbursement: scope.clientDisbursement,
          progress: informationProgressValue,
          clientProgress: clientDisbursement ? (clientDisbursement / (budget || 1)) * 100 : null,
        }) as InspectionScopeItemGridRowType;
      }).sort((a, b) => a.displayIndex - b.displayIndex);
      return { ...state, rows };
    },
    updateDrawBudget: (state, action: PayloadAction<MutateInspectionScopeItemMutation>) => {
      const { id, ...updates } = action.payload.updateInspectionScopeItem?.inspectionScopeItem as InspectionScopeItemType;
      const index = state.rows.findIndex((row) => row.id === id);
      if (index === -1) return state;

      state.rows[index] = {
        ...state.rows[index],
        ...updates,
        clientProgress: updates.clientDisbursement ? (updates.clientDisbursement / (updates.budget || 1)) * 100 : null,
      };
      return state;
    },
  },
});

export const drawBudgetActions = drawBudgetSlice.actions;

export default drawBudgetSlice.reducer;

// selectors
export const selectDrawBudget = (state: RootState) => state.drawBudget.rows;

export const selectDrawBudgetTotal = createSelector(
  [selectDrawBudget],
  (rows) => rows.reduce((totals, unit) => {
    totals.totalBudget += unit.budget || 0;
    totals.totalPreviousDisbursement += unit.previousDisbursement || 0;
    totals.totalRemainingBalance += (unit.budget || 0) - (unit.previousDisbursement || 0);
    totals.totalDisbursement += unit.disbursement || 0;
    totals.totalClientDisbursement += (unit.clientDisbursement === null ? unit.disbursement : unit.clientDisbursement) || 0;
    return totals;
  }, {
    totalBudget: 0,
    totalDisbursement: 0,
    totalPreviousDisbursement: 0,
    totalRemainingBalance: 0,
    totalClientDisbursement: 0,
  })
);

export const selectScopeItem = createSelector(
  [selectDrawBudget, (_, id: string) => id],
  (rows, id) => rows.find((row) => row.id === id)
);
